/* ==========================================================================
   Mairaz - Extended Size Upgrade   (assets/mairaz-extended-size.js)
   --------------------------------------------------------------------------
   Keeps the cart holding exactly ONE "Extended Size Upgrade" unit ($60) for
   every qualifying XL / XXL / XXXL Standard Stitched or Custom Stitched
   article in the cart.

   QUALIFYING RULE (both must be true)
     TYPE : Standard Stitched  OR  Custom Stitched
     SIZE : XL / XXL / XXXL  (also 1XL, 2XL, 3XL, X-Large, XX-Large,
            XXX-Large, Extra Large, Double XL, Triple XL)

   NEVER QUALIFIES
     XS, S, M, L, Large, One Size, Ready Shawl, Unstitched,
     and the Extended Size Upgrade / Express Delivery Upgrade lines themselves.

   This file is completely independent of assets/mairaz-express-fee.js and
   must never modify the Express Delivery Upgrade line.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.mairazExtendedSize;
  if (!CFG || window.mairazExtendedSizeReady) return;
  window.mairazExtendedSizeReady = true;

  function log(message, detail) {
    if (window.console && window.console.warn) {
      window.console.warn('[mairaz-extended-size] ' + message, detail === undefined ? '' : detail);
    }
  }

  var VARIANT_ID = Number(CFG.variantId);
  if (!CFG.variantId || !isFinite(VARIANT_ID) || VARIANT_ID <= 0) {
    log('variantId is missing or not a usable numeric variant id; the Extended Size Upgrade is INACTIVE.', CFG.variantId);
    return;
  }

  var nativeFetch = (typeof window.fetch === 'function') ? window.fetch.bind(window) : null;
  if (!nativeFetch || typeof window.Promise !== 'function') {
    log('fetch/Promise unavailable; the Extended Size Upgrade is INACTIVE.');
    return;
  }

  var R = CFG.routes || {};
  var CART_JS = (R.cart || '/cart') + '.js';
  var ADD_JS = (R.add || '/cart/add') + '.js';
  var CHANGE_JS = (R.change || '/cart/change') + '.js';

  var DEBOUNCE_MS = 150;
  var FETCH_TIMEOUT_MS = 10000;
  var MAX_WRITES_PER_CYCLE = 5;
  var MAX_CONSECUTIVE_FAILURES = 3;
  var DISABLED_FOR_MS = 30000;
  var CHECKOUT_MAX_WAIT_MS = 6000;

  function nowMs() { return Date.now ? Date.now() : new Date().getTime(); }
  function has(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }

  /* ------------------------------------------------------------------------
     1. QUALIFICATION
     --------------------------------------------------------------------- */

  var SIZE_ALIASES = {
    'XL': 1, '1XL': 1, 'XLARGE': 1, 'EXTRALARGE': 1,
    'XXL': 1, '2XL': 1, 'XXLARGE': 1, '2XLARGE': 1,
    'DOUBLEXL': 1, 'DOUBLEXLARGE': 1, 'DOUBLEEXTRALARGE': 1,
    'XXXL': 1, '3XL': 1, 'XXXLARGE': 1, '3XLARGE': 1,
    'TRIPLEXL': 1, 'TRIPLEXLARGE': 1, 'TRIPLEEXTRALARGE': 1
  };

  var SIZE_NEVER = {
    '': 1, 'XS': 1, 'S': 1, 'M': 1, 'L': 1,
    'XSMALL': 1, 'EXTRASMALL': 1, 'SMALL': 1, 'MEDIUM': 1, 'LARGE': 1,
    'ONESIZE': 1, 'FREESIZE': 1, 'READYSHAWL': 1, 'UNSTITCHED': 1,
    'STANDARD': 1, 'DEFAULT': 1, 'DEFAULTTITLE': 1,
    'CHOOSESIZE': 1, 'SELECTSIZE': 1, 'NA': 1, 'NONE': 1
  };

  var TYPE_QUALIFIES = { 'STANDARDSTITCHED': 1, 'CUSTOMSTITCHED': 1 };
  var SERVICE_PRODUCT_TYPES = { 'SIZINGSERVICE': 1, 'DELIVERYSERVICE': 1 };

  function squash(value) {
    if (value === null || value === undefined) return '';
    return String(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function sizeQualifies(rawSize) {
    var key = squash(rawSize);
    if (!key) return false;
    if (has(SIZE_NEVER, key)) return false;
    return has(SIZE_ALIASES, key);
  }

  function typeQualifies(rawType) {
    var key = squash(rawType);
    if (!key) return false;
    return has(TYPE_QUALIFIES, key);
  }

  function typeQualifiesFromTitle(title) {
    var parts = String(title === null || title === undefined ? '' : title).split('/');
    for (var i = 0; i < parts.length; i++) {
      if (typeQualifies(parts[i])) return true;
    }
    return false;
  }

  function propertyValue(properties, wantedKey) {
    if (!properties) return '';
    for (var key in properties) {
      if (!has(properties, key)) continue;
      if (String(key).toLowerCase().replace(/[^a-z]/g, '') === wantedKey) return properties[key];
    }
    return '';
  }

  function lineTypeValue(item) {
    var values = item.options_with_values || [];
    for (var i = 0; i < values.length; i++) {
      var name = String(values[i].name || '').toLowerCase().replace(/[^a-z]/g, '');
      if (name === 'type') return values[i].value;
    }
    return null;
  }

  function isSurchargeLine(item) {
    return Number(item.variant_id) === VARIANT_ID;
  }

  function lineQualifies(item) {
    if (!item) return false;
    if (isSurchargeLine(item)) return false;
    if (CFG.productHandle && String(item.handle || '') === String(CFG.productHandle)) return false;
    if (has(SERVICE_PRODUCT_TYPES, squash(item.product_type))) return false;
    if (!sizeQualifies(propertyValue(item.properties, 'size'))) return false;
    var typeValue = lineTypeValue(item);
    if (typeValue === null) return typeQualifiesFromTitle(item.variant_title);
    return typeQualifies(typeValue);
  }

  /* ------------------------------------------------------------------------
     2. NETWORK (uses the pre-hook fetch so our own writes never re-trigger us)
     --------------------------------------------------------------------- */

  function requestJSON(url, options) {
    options = options || {};
    var init = {
      method: options.method || 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    };
    if (options.body !== undefined) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('timeout'));
      }, FETCH_TIMEOUT_MS);
      nativeFetch(url, init).then(function (response) {
        return response.text().then(function (text) {
          var data = null;
          try { data = text ? JSON.parse(text) : null; } catch (parseError) { data = null; }
          if (!response.ok) {
            var httpError = new Error('HTTP ' + response.status);
            httpError.data = data;
            throw httpError;
          }
          return data;
        });
      }).then(function (data) {
        if (settled) return;
        settled = true; window.clearTimeout(timer); resolve(data);
      }, function (error) {
        if (settled) return;
        settled = true; window.clearTimeout(timer); reject(error);
      });
    });
  }

  /* ------------------------------------------------------------------------
     3. CART STATE - the real Shopify cart is the single source of truth
     --------------------------------------------------------------------- */

  var lastState = null;

  function readCartState() {
    return requestJSON(CART_JS, { method: 'GET' }).then(function (cart) {
      var items = (cart && cart.items) || [];
      var required = 0;
      var current = 0;
      var lines = [];
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (isSurchargeLine(item)) {
          lines.push({ key: item.key, quantity: Number(item.quantity) || 0 });
          current += Number(item.quantity) || 0;
          continue;
        }
        if (lineQualifies(item)) required += Number(item.quantity) || 0;
      }
      lastState = {
        itemCount: (cart && cart.item_count) || 0,
        required: required,
        current: current,
        lines: lines
      };
      return lastState;
    });
  }

  /* ------------------------------------------------------------------------
     4. SYNCHRONISATION ENGINE (single in-flight cycle, debounced, self-healing)
     --------------------------------------------------------------------- */

  var busy = false;
  var requeue = false;
  var debounceTimer = null;
  var pending = null;
  var failures = 0;
  var disabledUntil = 0;
  var verified = false;

  function scheduleSync(reason) {
    verified = false;
    if (debounceTimer) return pending || Promise.resolve(false);
    debounceTimer = window.setTimeout(function () {
      debounceTimer = null;
      runSync(reason);
    }, DEBOUNCE_MS);
    return pending || Promise.resolve(false);
  }

  function syncNow(reason) {
    if (debounceTimer) { window.clearTimeout(debounceTimer); debounceTimer = null; }
    return runSync(reason || 'manual');
  }

  function runSync(reason) {
    if (nowMs() < disabledUntil) return Promise.resolve(false);
    if (busy) { requeue = true; return pending || Promise.resolve(false); }

    busy = true;
    verified = false;
    setLocked(true);

    var writes = 0;
    var changed = false;

    function pass() {
      return readCartState().then(function (state) {
        if (state.lines.length > 1) {
          if (writes >= MAX_WRITES_PER_CYCLE) return false;
          writes += 1;
          return requestJSON(CHANGE_JS, {
            method: 'POST',
            body: { id: state.lines[state.lines.length - 1].key, quantity: 0 }
          }).then(function () { changed = true; return pass(); });
        }

        var line = state.lines[0] || null;
        var currentQuantity = line ? line.quantity : 0;
        if (currentQuantity === state.required) return true;

        if (writes >= MAX_WRITES_PER_CYCLE) {
          log('the cart would not accept the required Extended Size Upgrade quantity.', { required: state.required, inCart: currentQuantity });
          return false;
        }
        writes += 1;

        var write;
        if (line) {
          write = requestJSON(CHANGE_JS, { method: 'POST', body: { id: line.key, quantity: state.required } });
        } else {
          write = requestJSON(ADD_JS, { method: 'POST', body: { items: [{ id: VARIANT_ID, quantity: state.required }] } });
        }
        return write.then(function () { changed = true; return pass(); });
      });
    }

    pending = pass().then(function (ok) {
      busy = false;
      failures = 0;
      verified = !!ok;
      setLocked(false);
      if (changed) { refreshCartUI(); } else { decorateCart(); }
      if (requeue) { requeue = false; return runSync('requeue'); }
      return verified;
    }, function (error) {
      busy = false;
      verified = false;
      failures += 1;
      if (failures >= MAX_CONSECUTIVE_FAILURES) {
        disabledUntil = nowMs() + DISABLED_FOR_MS;
        log('paused for ' + (DISABLED_FOR_MS / 1000) + 's after repeated cart failures.', error && error.message);
      }
      setLocked(false);
      if (requeue) { requeue = false; return runSync('requeue'); }
      return false;
    });

    return pending;
  }

  /* ------------------------------------------------------------------------
     5. CART UI REFRESH (Shopify Section Rendering API, Dawn-compatible)
     --------------------------------------------------------------------- */

  var suppressObserver = 0;

  function sectionTargets() {
    var targets = [];
    var items = document.getElementById('main-cart-items');
    if (items && items.getAttribute('data-id')) {
      targets.push({ id: 'main-cart-items', section: items.getAttribute('data-id'), selector: '.js-contents' });
    }
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.getAttribute('data-id')) {
      targets.push({ id: 'main-cart-footer', section: footer.getAttribute('data-id'), selector: '.js-contents' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    if (document.getElementById('cart-live-region-text')) {
      targets.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }
        if (document.getElementById('cart-notification-button')) {
      targets.push({ id: 'cart-notification-button', section: 'cart-notification-button', selector: '.shopify-section' });
    }
    return targets;
  }

  function sectionsUrl(names) {
    var separator = location.search ? '&' : '?';
    return location.pathname + location.search + separator + 'sections=' + encodeURIComponent(names.join(','));
  }

  function refreshCartUI() {
    var targets = sectionTargets();
    if (!targets.length) { decorateCart(); return Promise.resolve(); }

    var names = [];
    for (var i = 0; i < targets.length; i++) names.push(targets[i].section);

    suppressObserver += 1;
    return nativeFetch(sectionsUrl(names), { credentials: 'same-origin' }).then(function (response) {
      return response.json();
    }).then(function (sections) {
      var parser = new DOMParser();
      for (var j = 0; j < targets.length; j++) {
        var target = targets[j];
        var html = sections[target.section];
        if (!html) continue;
        var host = document.getElementById(target.id);
        if (!host) continue;
        var parsed = parser.parseFromString(html, 'text/html');
        var source = parsed.querySelector(target.selector);
        if (!source) continue;
        var destination = host.querySelector(target.selector) || host;
        destination.innerHTML = source.innerHTML;
      }
      applyEmptyState();
      decorateCart();
    }).catch(function (error) {
      log('cart UI refresh failed.', error && error.message);
      decorateCart();
    }).then(function () {
      window.setTimeout(function () { suppressObserver = Math.max(0, suppressObserver - 1); }, 400);
    });
  }

  function applyEmptyState() {
    if (!lastState) return;
    var empty = lastState.itemCount === 0;
    var cartElement = document.querySelector('cart-items') || document.querySelector('.cart');
    if (cartElement && cartElement.classList) cartElement.classList.toggle('is-empty', empty);
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.classList) footer.classList.toggle('is-empty', empty);
  }

  /* ------------------------------------------------------------------------
     6. CART LINE PRESENTATION
     --------------------------------------------------------------------- */

  var STYLE_ID = 'mairaz-extended-size-style';

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.appendChild(document.createTextNode([
      '.mrz-esu-line .cart-item__quantity-wrapper quantity-input,',
      '.mrz-esu-line .cart-item__quantity-wrapper quantity-popover-container,',
      '.mrz-esu-line quantity-input,',
      '.mrz-esu-line cart-remove-button { display: none !important; }',
      '.mrz-esu-line .mrz-esu-qty { display: block; font-size: 0.9em; opacity: 0.85; white-space: nowrap; }',
      '.mrz-esu-line .mrz-esu-note { display: block; font-size: 0.9em; opacity: 0.85; margin-top: 0.2rem; }',
      '.mrz-esu-syncing { pointer-events: none !important; opacity: 0.55 !important; }'
    ].join('\n')));
    (document.head || document.documentElement).appendChild(style);
  }

  function surchargeRows() {
    var rows = [];
    var needle = String(VARIANT_ID);
    var links = document.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      if (href.indexOf(needle) < 0) continue;
      if (href.indexOf('/cart/change') < 0 && href.indexOf('/products/') < 0) continue;
      if (!links[i].closest) continue;
      var row = links[i].closest('.cart-item, tr.cart-item, .cart-notification-product');
      if (row && rows.indexOf(row) < 0) rows.push(row);
    }
    return rows;
  }

  function decorateCart() {
    suppressObserver += 1;
    window.setTimeout(function () { suppressObserver = Math.max(0, suppressObserver - 1); }, 0);
    injectStyles();
    var rows = surchargeRows();
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (row.classList) row.classList.add('mrz-esu-line');

      var controls = row.querySelectorAll('input[name="updates[]"], quantity-input input, .quantity__button');
      for (var j = 0; j < controls.length; j++) {
        controls[j].setAttribute('disabled', 'disabled');
        controls[j].setAttribute('tabindex', '-1');
        controls[j].setAttribute('aria-hidden', 'true');
      }

      var removers = row.querySelectorAll('cart-remove-button a, cart-remove-button button');
      for (var k = 0; k < removers.length; k++) {
        removers[k].setAttribute('tabindex', '-1');
        removers[k].setAttribute('aria-hidden', 'true');
        removers[k].removeAttribute('href');
      }

      var details = row.querySelector('.cart-item__details') || row;
      if (details && !details.querySelector('.mrz-esu-note')) {
        var note = document.createElement('span');
        note.className = 'mrz-esu-note';
        note.appendChild(document.createTextNode(CFG.lineSubLabel || 'For XL, XXL and XXXL sizing'));
        var anchor = details.querySelector('.cart-item__title');
        if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(note, anchor.nextSibling);
        else details.appendChild(note);
      }

      var quantityCell = row.querySelector('.cart-item__quantity');
      if (quantityCell && lastState) {
        var readout = quantityCell.querySelector('.mrz-esu-qty');
        if (!readout) {
          readout = document.createElement('span');
          readout.className = 'mrz-esu-qty';
          quantityCell.appendChild(readout);
        }
        readout.textContent = 'Qty ' + (lastState.current || 0);
      }
    }
  }

  /* ------------------------------------------------------------------------
     7. CHECKOUT PROTECTION
     --------------------------------------------------------------------- */

  var CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"], #checkout, .cart__checkout-button';
  var ACCELERATED_SELECTOR = '.cart__dynamic-checkout-buttons, .additional-checkout-buttons, .shopify-payment-button';

  function setLocked(locked) {
    var nodes = document.querySelectorAll(CHECKOUT_SELECTOR + ', ' + ACCELERATED_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList) continue;
      if (locked) nodes[i].classList.add('mrz-esu-syncing');
      else nodes[i].classList.remove('mrz-esu-syncing');
    }
  }

  function isSynchronised() {
    return verified === true && busy === false && debounceTimer === null;
  }

  window.addEventListener('click', function (event) {
    var origin = event.target;
    if (!origin || !origin.closest) return;
    var button = origin.closest(CHECKOUT_SELECTOR);
    if (!button) return;

    if (button.getAttribute('data-mrz-esu-go') === '1') {
      button.removeAttribute('data-mrz-esu-go');
      return;
    }
    // Always re-validate against the live Shopify cart before checkout proceeds.

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    var released = false;
    function release() {
      if (released) return;
      released = true;
      button.setAttribute('data-mrz-esu-go', '1');
      if (typeof button.click === 'function') button.click();
      else if (button.form && typeof button.form.submit === 'function') button.form.submit();
    }
    window.setTimeout(release, CHECKOUT_MAX_WAIT_MS);
    syncNow('checkout').then(release, release);
  }, true);

  /* ------------------------------------------------------------------------
     8. DETECT EVERY CART MUTATION MADE BY THE THEME OR THE CUSTOMER
     --------------------------------------------------------------------- */

  var CART_WRITE_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function isCartWrite(url) {
    try { return CART_WRITE_PATTERN.test(String(url)); } catch (error) { return false; }
  }

  (function hookFetch() {
    if (typeof window.fetch !== 'function') return;
    var previousFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      var write = isCartWrite(url) && String(method).toUpperCase() === 'POST';
      var result = previousFetch.apply(this, arguments);
      if (write && result && typeof result.then === 'function') {
        result.then(function (response) {
          window.setTimeout(function () { syncNow('cart-write'); }, 0);
          return response;
        }, function (error) { return error; });
      }
      return result;
    };
  })();

  (function hookXHR() {
    if (!window.XMLHttpRequest || !window.XMLHttpRequest.prototype) return;
    var proto = window.XMLHttpRequest.prototype;
    var open = proto.open;
    var send = proto.send;
    if (typeof open !== 'function' || typeof send !== 'function') return;
    proto.open = function (method, url) {
      this.__mrzEsuWrite = isCartWrite(url) && String(method || '').toUpperCase() === 'POST';
      return open.apply(this, arguments);
    };
    proto.send = function () {
      var request = this;
      if (request.__mrzEsuWrite) {
        request.addEventListener('loadend', function () { syncNow('cart-write-xhr'); });
      }
      return send.apply(this, arguments);
    };
  })();

  function observeCart() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function () {
      if (suppressObserver > 0) return;
      decorateCart();
      scheduleSync('dom-change');
    });
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) observer.observe(bubble, { childList: true, subtree: true, characterData: true });
    var items = document.getElementById('main-cart-items');
    if (items) observer.observe(items, { childList: true, subtree: true });
    var notification = document.querySelector('cart-notification');
    if (notification) observer.observe(notification, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     9. PRODUCT PAGE PRICE DISPLAY  (visual only - the real charge is the
        Extended Size Upgrade cart line)
     --------------------------------------------------------------------- */

  var PRODUCT = CFG.product || null;
  var priceTimer = null;

  function productForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (String(forms[i].className || '').indexOf('installment') > -1) continue;
      return forms[i];
    }
    return null;
  }

  function selectedVariantId() {
    var form = productForm();
    if (!form) return null;
    var field = form.querySelector('input[name="id"], select[name="id"]');
    return field ? String(field.value) : null;
  }

  function selectedSizeValue() {
    var nodes = document.querySelectorAll('[name="properties[Size]"]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.disabled) continue;
      if (node.type === 'radio' || node.type === 'checkbox') {
        if (node.checked) return node.value;
        continue;
      }
      return node.value;
    }
    return '';
  }

  function priceHost() {
    return document.querySelector('product-info [id^="price-"]') ||
           document.querySelector('.product__info-container [id^="price-"]') ||
           document.querySelector('.product__info-wrapper [id^="price-"]');
  }

  function overwrite(node, text) {
    if (!node) return;
    if (node.getAttribute('data-mrz-esu-base') === null) {
      node.setAttribute('data-mrz-esu-base', node.textContent);
    }
    node.textContent = text;
  }

  function restore(node) {
    var base = node.getAttribute('data-mrz-esu-base');
    if (base === null) return;
    node.textContent = base;
    node.removeAttribute('data-mrz-esu-base');
  }

  function updatePriceDisplay() {
    if (!PRODUCT || !PRODUCT.variants) return;
    var host = priceHost();
    if (!host) return;

    var marked = host.querySelectorAll('[data-mrz-esu-base]');
    var variantId = selectedVariantId();
    var data = variantId ? PRODUCT.variants[variantId] : null;
    var qualifies = !!(data && typeQualifies(data.type) && sizeQualifies(selectedSizeValue()));

    if (!qualifies) {
      for (var i = 0; i < marked.length; i++) restore(marked[i]);
      host.removeAttribute('data-mrz-esu-applied');
      return;
    }

    if (host.getAttribute('data-mrz-esu-applied') === variantId && marked.length > 0) return;
    for (var j = 0; j < marked.length; j++) restore(marked[j]);

    var regular = host.querySelectorAll('.price__regular .price-item--regular');
    for (var k = 0; k < regular.length; k++) overwrite(regular[k], data.plus);

    var sale = host.querySelectorAll('.price__sale .price-item--sale');
    for (var m = 0; m < sale.length; m++) overwrite(sale[m], data.plus);

    if (data.hasCmp) {
      var struck = host.querySelectorAll('.price__sale s.price-item--regular');
      for (var n = 0; n < struck.length; n++) overwrite(struck[n], data.cmpPlus);
    }

    host.setAttribute('data-mrz-esu-applied', variantId);
  }

  function schedulePriceUpdate() {
    if (priceTimer) return;
    priceTimer = window.setTimeout(function () {
      priceTimer = null;
      updatePriceDisplay();
    }, 40);
  }

  function bindProductPage() {
    if (!PRODUCT) return;
    document.addEventListener('change', schedulePriceUpdate, true);
    document.addEventListener('input', schedulePriceUpdate, true);
    document.addEventListener('click', schedulePriceUpdate, true);

    if (window.MutationObserver) {
      var container = document.querySelector('product-info') ||
                      document.querySelector('.product__info-container');
      if (container) {
        new MutationObserver(function () { schedulePriceUpdate(); })
          .observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled', 'value'] });
      }
    }

    /* Accelerated checkout must never skip the cart on a size-selectable
       product. There are currently no dynamic checkout buttons on product
       pages; this keeps the loophole closed if they are ever re-enabled. */
    if (document.querySelector('[name="properties[Size]"]')) {
      var accelerated = document.querySelectorAll('product-info .shopify-payment-button, .product__info-container .shopify-payment-button');
      for (var i = 0; i < accelerated.length; i++) {
        accelerated[i].style.display = 'none';
        accelerated[i].setAttribute('data-mrz-esu-hidden', 'true');
      }
    }
  }

  /* ------------------------------------------------------------------------
     10. BOOT
     --------------------------------------------------------------------- */

  function boot() {
    injectStyles();
    bindProductPage();
    observeCart();
    updatePriceDisplay();
    scheduleSync('page-load');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) syncNow('pageshow');
    schedulePriceUpdate();
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) scheduleSync('visibility');
  });

  window.mairazExtendedSizeAPI = {
    sync: syncNow,
    state: function () { return lastState; },
    lineQualifies: lineQualifies,
    sizeQualifies: sizeQualifies,
    typeQualifies: typeQualifies,
    updatePriceDisplay: updatePriceDisplay,
    isSynchronised: isSynchronised,
    variantId: VARIANT_ID
  };
})();
